import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { fetchOrderById, updateOrder } from "../../api/OrderAPIs";
import { fetchDesignById, fetchDesignByCode } from "../../api/DesignAPIs";
import { fetchTypes, fetchTypeById, fetchCaseStylesForType } from "../../api/TypeAPIs";
import { displayAr } from "../Catalog/catalogUi";
import { toast } from "react-toastify";
import { Loader, Plus, Trash2, X, Save, Search } from "lucide-react";

const PLACEHOLDER_PHONES = new Set(["غير معروف", "unknown phone", "unknown"]);

const normalizeOptionalPhone = (value) => {
  const phone = String(value || "").trim();
  if (!phone || PLACEHOLDER_PHONES.has(phone.toLowerCase())) return "";
  return phone;
};

const emptyDetail = () => ({
  orderDetailsId: null,
  designId: "",
  typeId: "",
  caseStyleId: "",
  quantity: 1,
  price: 0,
  designLabel: "",
  typeLabel: "",
  designCode: "",
});

const typeSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "60px",
    borderRadius: "0.75rem",
    borderColor: state.isFocused ? "#7c3aed" : "#d1d5db",
    boxShadow: "none",
    direction: "rtl",
  }),
  menu: (provided) => ({
    ...provided,
    direction: "rtl",
    zIndex: 50,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#7c3aed"
      : state.isFocused
        ? "#f3e8ff"
        : "white",
    color: state.isSelected ? "white" : "#111827",
    textAlign: "right",
    cursor: "pointer",
  }),
  singleValue: (provided) => ({
    ...provided,
    textAlign: "right",
  }),
  placeholder: (provided) => ({
    ...provided,
    textAlign: "right",
    color: "#9ca3af",
  }),
  input: (provided) => ({
    ...provided,
    direction: "rtl",
  }),
};

const EditOrderModal = ({ orderId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [stylesByType, setStylesByType] = useState({});
  const [form, setForm] = useState({
    name: "",
    phoneNumber: "",
    phoneNumber2: "",
    totalAmount: 0,
    discount: 0,
    profit: 0,
    shippingAmount: 0,
    note: "",
    state: "",
    city: "",
    apartment: "",
    fullAddress: "",
    details: [],
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [order, allTypes] = await Promise.all([
          fetchOrderById(orderId),
          fetchTypes(),
        ]);

        if (cancelled) return;

        const details = await Promise.all(
          (order.orderDetails || []).map(async (d) => {
            let designLabel = `Design #${d.designId}`;
            let designCode = "";
            let typeLabel = `Type #${d.typeId}`;
            try {
              const design = await fetchDesignById(d.designId);
              designLabel = design?.Name || design?.name || designLabel;
              designCode = design?.code || design?.Code || "";
            } catch (_) {}
            try {
              const type = await fetchTypeById(d.typeId);
              typeLabel = type?.name || type?.Name || typeLabel;
            } catch (_) {}

            return {
              orderDetailsId: d.orderDetailsId,
              designId: d.designId,
              typeId: d.typeId,
              caseStyleId: d.caseStyleId || d.caseStyle?.caseStyleId || "",
              quantity: d.quantity,
              price: d.price,
              designLabel,
              typeLabel,
              designCode,
            };
          })
        );

        const typeIds = [...new Set(details.map((d) => d.typeId).filter(Boolean))];
        const styleEntries = await Promise.all(
          typeIds.map(async (typeId) => {
            try {
              const styles = await fetchCaseStylesForType(typeId);
              return [String(typeId), styles];
            } catch {
              return [String(typeId), []];
            }
          })
        );
        setStylesByType(Object.fromEntries(styleEntries));

        setTypes(Array.isArray(allTypes) ? allTypes : []);
        setForm({
          name: order.name || "",
          phoneNumber: order.phoneNumber || "",
          phoneNumber2: normalizeOptionalPhone(order.phoneNumber2),
          totalAmount: Number(order.totalAmount) || 0,
          discount: Number(order.discount) || 0,
          profit: Number(order.profit) || 0,
          shippingAmount: Number(order.shippingAmount) || 0,
          note: order.note || "",
          state: order.address?.state || "",
          city: order.address?.city || "",
          apartment: order.address?.apartment || "",
          fullAddress: order.address?.fullAddress || "",
          details: details.length > 0 ? details : [emptyDetail()],
        });
      } catch (err) {
        console.error(err);
        toast.error(err.message || "فشل تحميل الطلب");
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [orderId, onClose]);

  const typeOptions = useMemo(
    () =>
      (types || []).map((t) => {
        const id = t.typeId || t.TypeId || t.id;
        const name = t.name || t.Name || `Type #${id}`;
        return { value: String(id), label: name };
      }),
    [types]
  );

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateDetail = (index, key, value) => {
    setForm((prev) => {
      const details = [...prev.details];
      details[index] = { ...details[index], [key]: value };
      return { ...prev, details };
    });
  };

  const resolveDesignByCode = async (index) => {
    const code = String(form.details[index]?.designCode || "").trim();
    if (!code) return;

    try {
      const design = await fetchDesignByCode(code);
      const designId = design?.designId || design?.DesignId || design?.id;
      const name = design?.Name || design?.name || "";
      if (!designId) {
        toast.error(`لم يتم العثور على ديزاين بالكود: ${code}`);
        return;
      }
      setForm((prev) => {
        const details = [...prev.details];
        details[index] = {
          ...details[index],
          designId,
          designLabel: name || `Design #${designId}`,
          designCode: design?.code || design?.Code || code,
        };
        return { ...prev, details };
      });
      toast.success(`تم ربط الديزاين: ${name || designId}`);
    } catch (err) {
      toast.error(err.message || "فشل البحث عن الديزاين");
    }
  };

  const addDetail = () => {
    setForm((prev) => ({ ...prev, details: [...prev.details, emptyDetail()] }));
  };

  const removeDetail = (index) => {
    setForm((prev) => {
      if (prev.details.length <= 1) {
        toast.info("لازم تفضل قطعة واحدة على الأقل");
        return prev;
      }
      return {
        ...prev,
        details: prev.details.filter((_, i) => i !== index),
      };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("الاسم مطلوب");
      return;
    }
    if (!form.phoneNumber.trim()) {
      toast.error("رقم الهاتف مطلوب");
      return;
    }

    for (let i = 0; i < form.details.length; i++) {
      const d = form.details[i];
      if (!Number(d.designId) || !Number(d.typeId)) {
        toast.error(`القطعة ${i + 1}: DesignId و TypeId مطلوبين`);
        return;
      }
      if (Number(d.quantity) <= 0) {
        toast.error(`القطعة ${i + 1}: الكمية لازم تكون أكبر من صفر`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phoneNumber: form.phoneNumber.trim(),
        phoneNumber2: normalizeOptionalPhone(form.phoneNumber2),
        totalAmount: Number(form.totalAmount) || 0,
        discount: Number(form.discount) || 0,
        profit: Number(form.profit) || 0,
        shippingAmount: Number(form.shippingAmount) || 0,
        note: form.note || "",
        address: {
          state: form.state || "",
          city: form.city || "",
          apartment: form.apartment || "",
          fullAddress: form.fullAddress || "",
        },
        orderDetails: form.details.map((d) => ({
          orderDetailsId: d.orderDetailsId || null,
          designId: Number(d.designId),
          typeId: Number(d.typeId),
          caseStyleId: Number(d.caseStyleId) || null,
          quantity: Number(d.quantity),
          price: Number(d.price) || 0,
        })),
      };

      const updated = await updateOrder(orderId, payload);
      toast.success(`تم تحديث الطلب #${orderId}`);
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast.error(err.message || "فشل حفظ التعديلات");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      dir="rtl"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-l from-purple-50 to-white">
          <div>
            <h2 className="text-lgxl font-bold text-lgpurple-800">تعديل الطلب #{orderId}</h2>
            <p className="text-lg text-gray-500">Admin فقط — تعديل كامل للبيانات والقطع والمبلغ</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-2 rounded-xl hover:bg-gray-100 disabled:opacity-50"
          >
            <X size={22} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5 flex-1">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-lgpurple-700">
              <Loader className="animate-spin" size={22} />
              جاري تحميل بيانات الطلب...
            </div>
          ) : (
            <>
              <section className="space-y-3">
                <h3 className="font-bold text-gray-800 border-b pb-1 text-3xl">بيانات العميل</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="text-lg">
                    <span className="block mb-1 font-medium">الاسم</span>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={form.name}
                      onChange={(e) => updateField("name", e.target.value)}
                    />
                  </label>
                  <label className="text-lg">
                    <span className="block mb-1 font-medium">الهاتف 1</span>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={form.phoneNumber}
                      onChange={(e) => updateField("phoneNumber", e.target.value)}
                    />
                  </label>
                  <label className="text-lg">
                    <span className="block mb-1 font-medium">الهاتف 2 (اختياري)</span>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={form.phoneNumber2}
                      onChange={(e) => updateField("phoneNumber2", e.target.value)}
                    />
                  </label>
                  {/* Total */}
                  <div className="md:col-span-4">
                    <label className="block">
                      <span className="block mb-2 text-2xl font-bold text-gray-800">
                        الإجمالي
                      </span>
                      <input
                        type="number"
                        className="w-full border rounded-xl px-4 py-4 text-2xl font-bold"
                        value={form.totalAmount}
                        onChange={(e) => updateField("totalAmount", e.target.value)}
                      />
                    </label>
                  </div>
                  <label className="text-lg md:col-span-2">
                    <span className="block mb-1 font-medium">ملاحظات</span>
                    <textarea
                      className="w-full border rounded-xl px-3 py-2 min-h-[70px]"
                      value={form.note}
                      onChange={(e) => updateField("note", e.target.value)}
                    />
                  </label>
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="font-bold text-gray-800 border-b pb-1 text-3xl">العنوان</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="text-lg">
                    <span className="block mb-1 font-medium">المحافظة</span>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={form.state}
                      onChange={(e) => updateField("state", e.target.value)}
                    />
                  </label>
                  <label className="text-lg">
                    <span className="block mb-1 font-medium">المدينة / المنطقة</span>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={form.city}
                      onChange={(e) => updateField("city", e.target.value)}
                    />
                  </label>
                  <label className="text-lg">
                    <span className="block mb-1 font-medium">علامة مميزة / شقة</span>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={form.apartment}
                      onChange={(e) => updateField("apartment", e.target.value)}
                    />
                  </label>
                  <label className="text-lg md:col-span-2">
                    <span className="block mb-1 font-medium">العنوان بالكامل</span>
                    <textarea
                      className="w-full border rounded-xl px-3 py-2 min-h-[70px]"
                      value={form.fullAddress}
                      onChange={(e) => updateField("fullAddress", e.target.value)}
                    />
                  </label>
                </div>
              </section>

            <section className="space-y-3">
              <h3 className="font-semibold text-gray-800 border-b pb-1 text-3xl">المبالغ</h3>

              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                {/* Total */}
                <div className="md:col-span-4">
                  <label className="block">
                    <span className="block mb-2 text-2xl font-bold text-gray-800">
                      الإجمالي
                    </span>
                    <input
                      type="number"
                      className="w-full border rounded-xl px-4 py-4 text-2xl font-bold"
                      value={form.totalAmount}
                      onChange={(e) => updateField("totalAmount", e.target.value)}
                    />
                  </label>
                </div>

                {/* Other amounts */}
                  <label className="block mt-2">
                    <span className="block mb-1 text-lg font-medium">الشحن</span>
                    <input
                      type="number"
                      className="w-full border rounded-xl  px-4 py-4 text-lg"
                      value={form.shippingAmount}
                      onChange={(e) => updateField("shippingAmount", e.target.value)}
                    />
                  </label>

                  <label className="block mt-2">
                    <span className="block mb-1 text-lg font-medium">الخصم</span>
                    <input
                      type="number"
                      className="w-full border rounded-xl  px-4 py-4 text-lg"
                      value={form.discount}
                      onChange={(e) => updateField("discount", e.target.value)}
                    />
                  </label>

                  {/* <label className="block">
                    <span className="block mb-1 text-lg font-medium">الربح</span>
                    <input
                      type="number"
                      className="w-full border rounded-xl px-3 py-2 text-lg"
                      value={form.profit}
                      onChange={(e) => updateField("profit", e.target.value)}
                    />
                  </label> */}
                </div>
            </section>

              <section className="space-y-3">
                <div className="flex items-center justify-between border-b pb-1 text-3xl">
                  <h3 className="font-bold text-gray-800">قطع الطلب</h3>
                  <button
                    type="button"
                    onClick={addDetail}
                    className="flex items-center gap-1 text-lg bg-purple-600 text-lgwhite px-3 py-1.5 rounded-lg hover:bg-purple-700"
                  >
                    <Plus size={16} />
                    إضافة قطعة
                  </button>
                </div>

                <div className="space-y-3">
                  {form.details.map((detail, index) => (
                    <div
                      key={`${detail.orderDetailsId || "new"}-${index}`}
                      className="border border-purple-100 rounded-xl p-3 bg-purple-50/40 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-lgpurple-800">قطعة {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeDetail(index)}
                          className="text-lgred-600 hover:bg-red-50 p-1.5 rounded-lg"
                          title="حذف القطعة"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
                        <label className="text-lg md:col-span-1">
                          <span className="block mb-1 font-medium">كود الديزاين (بحث)</span>
                          <div className="flex gap-2">
                            <input
                              className="flex-1 border rounded-xl px-3 py-2 text-2xl"
                              value={detail.designCode}
                              onChange={(e) => updateDetail(index, "designCode", e.target.value)}
                              placeholder="مثال: ABC123"
                            />
                            <button
                              type="button"
                              onClick={() => resolveDesignByCode(index)}
                              className="px-6 py-2 bg-orange-500 text-white border rounded-xl hover:bg-orange-300 flex items-center gap-1"
                            >
                              <Search size={16} />
                              بحث
                            </button>
                          </div>
                          {detail.designLabel && (
                            <p className="text-lgxs text-gray-600 mt-1">{detail.designLabel}</p>
                          )}
                        </label>

                       

                        <label className="text-lg md:col-span-1">
                          <span className="block mb-1 font-medium">نوع الجهاز</span>
                          <Select
                            options={typeOptions}
                            value={
                              typeOptions.find(
                                (opt) => String(opt.value) === String(detail.typeId)
                              ) || null
                            }
                            onChange={async (selected) => {
                              const typeId = selected?.value || "";
                              setForm((prev) => {
                                const details = [...prev.details];
                                details[index] = {
                                  ...details[index],
                                  typeId,
                                  typeLabel: selected?.label || "",
                                  caseStyleId: "",
                                };
                                return { ...prev, details };
                              });
                              if (typeId && !stylesByType[typeId]) {
                                try {
                                  const styles = await fetchCaseStylesForType(typeId);
                                  setStylesByType((prev) => ({ ...prev, [String(typeId)]: styles }));
                                } catch {
                                  setStylesByType((prev) => ({ ...prev, [String(typeId)]: [] }));
                                }
                              }
                            }}
                            placeholder="ابحث واختر نوع الجهاز..."
                            isSearchable
                            isClearable
                            styles={typeSelectStyles}
                            noOptionsMessage={() => "لا توجد نتائج مطابقة"}
                            filterOption={(option, input) =>
                              option.label
                                .toLowerCase()
                                .includes((input || "").trim().toLowerCase())
                            }
                            menuPlacement="auto"
                          />
                        </label>
                        <label className="text-lg md:col-span-2">
                          <span className="block mb-1 font-medium">نوع الكفر</span>
                          <select
                            className="w-full border rounded-xl px-3 py-2"
                            value={detail.caseStyleId || ""}
                            onChange={(e) => updateDetail(index, "caseStyleId", e.target.value)}
                          >
                            <option value="">بدون</option>
                            {(stylesByType[String(detail.typeId)] || []).map((style) => (
                              <option key={style.caseStyleId} value={style.caseStyleId}>
                                {displayAr(style.name)}
                                {Number(style.priceAdjustment) ? ` (${style.priceAdjustment > 0 ? "+" : ""}${style.priceAdjustment})` : ""}
                              </option>
                            ))}
                          </select>
                        </label>
                        </div>
                        <div className="grid grid-cols-3 md:grid-cols-3 gap-1">
                        <label className="text-lg">
                          <span className=" mb-1 font-medium">Design ID</span>
                          <input
                            type="number"
                            className="w-full border rounded-xl px-5 py-2"
                            value={detail.designId}
                            onChange={(e) => updateDetail(index, "designId", e.target.value)}
                          />
                        </label>
                        <label className="text-lg">
                          <span className=" mb-1 font-medium">الكمية</span>
                          <input
                            type="number"
                            min={1}
                            className="w-full border rounded-xl px-5 py-2"
                            value={detail.quantity}
                            onChange={(e) => updateDetail(index, "quantity", e.target.value)}
                          />
                        </label>

                        <label className="text-lg">
                          <span className=" mb-1 font-medium">سعر القطعة</span>
                          <input
                            type="number"
                            className="w-full border rounded-xl px-5 py-2"
                            value={detail.price}
                            onChange={(e) => updateDetail(index, "price", e.target.value)}
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl border bg-white hover:bg-gray-100 disabled:opacity-50"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className="px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-lgwhite font-bold flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader className="animate-spin" size={18} /> : <Save size={18} />}
            حفظ التعديلات
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditOrderModal;
